import { useState, useEffect } from 'react'
import { AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

interface ConfirmDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description: string
  confirmText?: string
  cancelText?: string
  variant?: 'destructive' | 'default'
  onConfirm: () => void
  onCancel?: () => void
  loading?: boolean
}

export default function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmText = 'ยืนยัน',
  cancelText = 'ยกเลิก',
  variant = 'destructive',
  onConfirm,
  onCancel,
  loading = false,
}: ConfirmDialogProps) {
  // Store props in local state
  const [dialogState, setDialogState] = useState({
    title: '',
    description: '',
    confirmText: 'ยืนยัน',
    cancelText: 'ยกเลิก',
  })

  // Update local state when props change and dialog opens
  useEffect(() => {
    if (open) {
      setDialogState({
        title,
        description,
        confirmText,
        cancelText,
      })
    }
  }, [open, title, description, confirmText, cancelText])

  const handleConfirm = () => {
    onConfirm()
    onOpenChange(false)
  }

  const handleCancel = () => {
    onCancel?.()
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[480px] p-0 overflow-hidden"
        aria-describedby=""
      >
        <DialogHeader className="px-6 pt-6 pb-4">
          <div className="flex items-start gap-4">
            <div className="hidden sm:flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-red-50 to-red-100 border border-red-200">
              <AlertTriangle className="h-7 w-7 text-red-600" />
            </div>
            <div className="flex-1 pt-1">
              <DialogTitle className="text-xl font-semibold text-gray-900 mb-2">
                {dialogState.title}
              </DialogTitle>
              <DialogDescription className="text-sm text-gray-600 leading-relaxed">
                {dialogState.description}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <DialogFooter className="px-6 pb-6 pt-4">
          <div className="flex gap-3 w-full">
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={loading}
              className="flex-1 h-11 px-6 font-medium border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400"
            >
              {dialogState.cancelText}
            </Button>
            <Button
              variant={variant}
              onClick={handleConfirm}
              disabled={loading}
              className="flex-1 h-11 px-6 font-medium shadow-sm hover:shadow-md transition-shadow"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  กำลังดำเนินการ...
                </div>
              ) : (
                dialogState.confirmText
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
